import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { FileUploadState } from '../models/FileUploadState';
import { FilesUploadsService } from '../services/files-uploads.service';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { ProgressBarComponent } from './progress-bar.component';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { AppIconsType } from '@fc-shared/ui/icon/icons';

@Component({
  selector: 'fc-files-upload-card',
  template: `
    <div [class]="statusClasses()" class="flex flex-col rounded-lg border p-12">
      <div class="flex flex-row items-start gap-16 justify-between">
        <div
          [class.bg-error-100]="failed()"
          [class.bg-primary-100]="!failed()"
          class="rounded-full p-10 flex items-center justify-center"
        >
          <fc-icon
            [icon]="statusIcon()"
            [color]="failed() ? 'error600' : 'primary600'"
          ></fc-icon>
        </div>

        <div class="flex flex-col gap-4 flex-1">
          <div class="flex flex-row justify-between">
            <div class="flex flex-col">
              <span
                class="text-sm text-grey-900 font-medium truncate max-w-[200px]"
                >{{ fileUploadState().fileName }}</span
              >
              <span class="text-sm text-secondary-500">{{
                fileUploadState().formattedFileSize
              }}</span>
            </div>
            @if (fileUploadState().status !== 'completed') {
              <fc-button
                variant="icon"
                (click)="deleteUpload()"
                icon="icon-delete"
                color="error"
              />
            }
          </div>
          @if (failed()) {
            <span (click)="retryUpload()" class="text-error-500 cursor-pointer"
              >Try again</span
            >
          } @else {
            <div class="flex flex-row gap-8 items-center">
              <fc-progress-bar
                class="flex-1"
                [progress]="fileUploadState().progress"
              ></fc-progress-bar>
              <span class="text-sm text-secondary-500"
                >{{ fileUploadState().progress }}%</span
              >
            </div>
          }
        </div>
      </div>
    </div>
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, ProgressBarComponent, ButtonComponent],
})
export class FilesUploadCardComponent {
  fileUploadState = input.required<FileUploadState>();
  completed = computed(() => this.fileUploadState().status === 'completed');
  failed = computed(() => this.fileUploadState().status === 'failed');
  statusIcon = computed<AppIconsType>(() => {
    if (this.completed()) {
      return 'icon-tick';
    }
    if (this.failed()) {
      return 'icon-close';
    }
    return 'icon-file';
  });
  private readonly filesUploadsService = inject(FilesUploadsService);

  statusClasses = computed(() => {
    if (this.completed()) {
      return 'bg-primary-100 border-primary-600';
    }
    if (this.failed()) {
      return 'bg-error-50 border-error-500';
    }
    return 'bg-white border-secondary-200';
  });

  retryUpload() {
    this.filesUploadsService.retryUpload(this.fileUploadState().id);
  }

  deleteUpload(): void {
    this.filesUploadsService.discardUpload(this.fileUploadState().id);
  }
}
